import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { User } from "../../types";
import * as Yup from "yup";
import {
  FormControl,
  FormErrorMessage,
  Input,
  Stack,
  Textarea,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import { Label } from "../Label";
import { DatePicker } from "../DatePicker";
import { PrimayButton } from "../Button";
import { useMemo, useRef, useState } from "react";
import provinces from "../../utils/province";
import Select from "react-select";

type Props = {
  user?: User;
  isSubmitting: boolean;
  onSubmit: (values: any) => void;
  editable?: boolean;
  isAdmin?: boolean;
  validable: boolean;
};

export const UserProfileForm: React.FC<Props> = ({
  user,
  isSubmitting,
  onSubmit,
  editable = true,
  isAdmin = false,
  validable,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const provincesItem = useMemo(() => {
    const categories: any = [
      { id: "", label: t("screens.board.event.noProjectCategory") },
    ];

    return [...categories, ...provinces];
  }, [t]);

  const assistanceTypeOptions = [
    {
      label: t(`screens.client.clientForm.assistanceTypes.none`),
      value: "none",
    },
    {
      label: t(`screens.client.clientForm.assistanceTypes.littleAssistance`),
      value: "littleAssistance",
    },
    {
      label: t(`screens.client.clientForm.assistanceTypes.needsAssistance`),
      value: "needsAssistance",
    },
  ];

  const genderOptions = [
    {
      label: t(`screens.client.clientForm.genderOptions.f`),
      value: "f",
    },
    {
      label: t(`screens.client.clientForm.genderOptions.m`),
      value: "m",
    },
  ];

  const contactPreferenceOptions: any = [
    {
      label: t(`screens.client.clientForm.contactPreferences.mobile`),
      value: "mobile",
    },
    {
      label: t(`screens.client.clientForm.contactPreferences.whatsapp`),
      value: "whatsapp",
    },
    {
      label: t(`screens.client.clientForm.contactPreferences.mail`),
      value: "mail",
    },
    {
      label: t(`screens.client.clientForm.contactPreferences.no`),
      value: "no",
    },
  ];

  const formik = useFormik({
    initialValues: {
      name: user?.name || "",
      surname: user?.surname || "",
      email: user?.email,
      phone: user?.phone || "",
      birth_date: user?.birth_date ? new Date(user.birth_date) : undefined,
      contact_preference: user?.contact_preference || "",
      contact_emergency: user?.contact_emergency || "",
      gender: user?.gender || "f",
      address: user?.address || "",
      province_state: user?.province_state || "",
      zip_code: user?.zip_code || "",
      city: user?.city || "",
      state: user?.state || "",
      assistance_type: user?.assistance_type || "",
      fb: user?.fb || "",
      ig: user?.ig || "",
      note: user?.note || "",
    },
    enableReinitialize: true,
    onSubmit: onSubmit,
    validationSchema: validable
      ? Yup.object().shape({
          name: Yup.string().required(
            t("screens.client.clientValidations.nameRequired")
          ),
          surname: Yup.string().required(
            t("screens.client.clientValidations.surnameRequired")
          ),
          phone: Yup.string().required(
            t("screens.client.clientValidations.phoneRequired")
          ),
          email: Yup.string()
            .email(t("screens.client.clientValidations.emailRequired"))
            .required(t("screens.client.clientValidations.emailRequired")),
          gender: Yup.string().matches(
            /(f)/,
            t("screens.client.clientValidations.genderRequired")
          ),
          birth_date: Yup.string().required(
            t("screens.client.clientValidations.birth_dateRequired")
          ),
          province_state: Yup.string().required(
            t("screens.client.clientValidations.provinceRequired")
          ),
          state: Yup.string().required(
            t("screens.client.clientValidations.stateRequired")
          ),
        })
      : Yup.object().shape({
          email: Yup.string()
            .email(t("screens.client.clientValidations.emailRequired"))
            .required(t("screens.client.clientValidations.emailRequired")),
        }),
  });

  const selectStyles = {
    control: (styles: any) => ({
      ...styles,
      height: "48px",
      borderRadius: "var(--chakra-radii-md)",
      borderColor: "inherit",
    }),
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack gridGap={"0.5rem"}>
        <Stack spacing={"1rem"} direction={["column", "row"]}>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("name").error) &&
              formik.getFieldMeta("name").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.name")}
            </Label>
            <Input
              size="lg"
              name={"name"}
              value={formik.values.name}
              onChange={formik.handleChange}
              disabled={!editable}
            />
            <FormErrorMessage>
              {formik.getFieldMeta("name").error}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("surname").error) &&
              formik.getFieldMeta("surname").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.surname")}
            </Label>
            <Input
              size="lg"
              name={"surname"}
              value={formik.values.surname}
              onChange={formik.handleChange}
              disabled={!editable}
            />
            <FormErrorMessage>
              {formik.getFieldMeta("surname").error}
            </FormErrorMessage>
          </FormControl>
        </Stack>
        <Stack spacing={"1rem"} direction={["column", "row"]}>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("email").error) &&
              formik.getFieldMeta("email").touched
            }
          >
            <Label fontWeight="900">{t("profile.email")}</Label>
            <Input
              size="lg"
              name={"email"}
              value={formik.values.email}
              onChange={formik.handleChange}
              disabled={!editable}
            />
            <FormErrorMessage>
              {formik.getFieldMeta("email").error}
            </FormErrorMessage>
          </FormControl>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("phone").error) &&
              formik.getFieldMeta("phone").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.phone")}
            </Label>
            <Input
              size="lg"
              name={"phone"}
              value={formik.values.phone}
              onChange={formik.handleChange}
              disabled={!editable}
            />
            <FormErrorMessage>
              {formik.getFieldMeta("phone").error}
            </FormErrorMessage>
          </FormControl>
        </Stack>
        <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
          <FormControl>
            <Label>{t("screens.client.clientForm.contact_preference")}</Label>
            {editable ? (
              <Select
                options={contactPreferenceOptions}
                value={{
                  label: formik.values.contact_preference
                    ? t(
                        `screens.client.clientForm.contactPreferences.${formik.values.contact_preference}`
                      )
                    : null,
                  value: formik.values.contact_preference
                    ? formik.values.contact_preference
                    : null,
                }}
                onChange={(option: any) => {
                  formik.setFieldValue("contact_preference", option.value);
                }}
                onBlur={formik.handleBlur}
                isSearchable={!editable}
                styles={selectStyles}
              />
            ) : (
              <Input
                size="lg"
                name={"contact_preference"}
                value={
                  formik.values.contact_preference
                    ? String(
                        t(
                          `screens.client.clientForm.contactPreferences.${formik.values.contact_preference}`
                        )
                      )
                    : ""
                }
                disabled={!editable}
              />
            )}
          </FormControl>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("gender").error) &&
              formik.getFieldMeta("gender").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.gender")}
            </Label>
            {editable ? (
              <>
                <Select
                  options={genderOptions}
                  value={{
                    label: t(
                      `screens.client.clientForm.genderOptions.${formik.values.gender}`
                    ),
                    value: formik.values.gender,
                  }}
                  onChange={(option: any) => {
                    if (option.value == "m") {
                      setIsOpen(true);
                    }
                    formik.setFieldValue("gender", option.value);
                  }}
                  onBlur={formik.handleBlur}
                  isSearchable={!editable}
                  styles={selectStyles}
                />
                <FormErrorMessage>
                  {formik.getFieldMeta("gender").error}
                </FormErrorMessage>
              </>
            ) : (
              <Input
                size="lg"
                name={"gender"}
                value={
                  formik.values.gender
                    ? String(
                        t(
                          `screens.client.clientForm.genderOptions.${formik.values.gender}`
                        )
                      )
                    : ""
                }
                disabled={!editable}
              />
            )}
          </FormControl>
        </Stack>
        <Stack spacing={"1rem"} direction={["column", "row"]}>
          <FormControl>
            <Label>{t("screens.client.clientForm.contact_emergency")}</Label>
            <Input
              size="lg"
              name={"contact_emergency"}
              value={formik.values.contact_emergency}
              onChange={formik.handleChange}
              disabled={!editable}
            />
          </FormControl>
          {isAdmin && (
            <FormControl>
              <Label>{t("screens.client.clientForm.assistance_type")}</Label>
              {editable ? (
                <Select
                  options={assistanceTypeOptions}
                  value={{
                    label: formik.values.assistance_type
                      ? t(
                          `screens.client.clientForm.assistanceTypes.${formik.values.assistance_type}`
                        )
                      : null,
                    value: formik.values.assistance_type
                      ? formik.values.assistance_type
                      : null,
                  }}
                  onChange={(option: any) => {
                    formik.setFieldValue("assistance_type", option.value);
                  }}
                  onBlur={formik.handleBlur}
                  isSearchable={!editable}
                  styles={selectStyles}
                />
              ) : (
                <Input
                  size="lg"
                  name={"assistance_type"}
                  value={
                    formik.values.assistance_type
                      ? String(
                          t(
                            `screens.client.clientForm.assistanceTypes.${formik.values.assistance_type}`
                          )
                        )
                      : ""
                  }
                  disabled={!editable}
                />
              )}
            </FormControl>
          )}
        </Stack>
        <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
          <FormControl>
            <Label>{t("screens.client.clientForm.address")}</Label>
            <Input
              size="lg"
              name={"address"}
              value={formik.values.address}
              onChange={formik.handleChange}
              disabled={!editable}
            />
          </FormControl>
          <FormControl>
            <Label>{t("screens.client.clientForm.city")}</Label>
            <Input
              size="lg"
              name={"city"}
              value={formik.values.city}
              onChange={formik.handleChange}
              disabled={!editable}
            />
          </FormControl>
        </Stack>
        <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("province_state").error) &&
              formik.getFieldMeta("province_state").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.province")}
            </Label>
            {editable ? (
              <>
                <Select
                  options={provincesItem}
                  value={{
                    label: formik.values.province_state,
                    value: formik.values.province_state,
                  }}
                  onChange={(option: any) => {
                    formik.setFieldValue("province_state", option.value);
                  }}
                  onBlur={formik.handleBlur}
                  isSearchable={!editable}
                  styles={selectStyles}
                />
                <FormErrorMessage>
                  {formik.getFieldMeta("province_state").error}
                </FormErrorMessage>
              </>
            ) : (
              <Input
                size="lg"
                name={"province_state"}
                value={formik.values.province_state}
                disabled={!editable}
              />
            )}
          </FormControl>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("state").error) &&
              formik.getFieldMeta("state").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.state")}
            </Label>
            <Input
              size="lg"
              name={"state"}
              value={formik.values.state}
              onChange={formik.handleChange}
              disabled={!editable}
            />
            <FormErrorMessage>
              {formik.getFieldMeta("state").error}
            </FormErrorMessage>
          </FormControl>
        </Stack>

        <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
          <FormControl>
            <Label>{t("screens.client.clientForm.zipCode")}</Label>
            <Input
              size="lg"
              name={"zip_code"}
              type="number"
              value={formik.values.zip_code}
              onChange={formik.handleChange}
              disabled={!editable}
            />
          </FormControl>
          <FormControl
            isInvalid={
              Boolean(formik.getFieldMeta("birth_date").error) &&
              formik.getFieldMeta("birth_date").touched
            }
          >
            <Label fontWeight="900">
              {t("screens.client.clientForm.birth_date")}
            </Label>
            <DatePicker
              // @ts-ignore
              dateFormat={"dd/MM/yyyy"}
              selectedDate={formik.values.birth_date || undefined}
              onChange={(date) => {
                formik.setFieldValue("birth_date", date || null);
              }}
              disabled={!editable}
              yearDropdownItemNumber={15}
              scrollableYearDropdown
            />
            <FormErrorMessage>
              {formik.getFieldMeta("birth_date").error}
            </FormErrorMessage>
          </FormControl>
        </Stack>

        <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
          <FormControl>
            <Label>{t("screens.client.clientForm.fb")}</Label>
            <Input
              size="lg"
              name={"fb"}
              value={formik.values.fb}
              onChange={formik.handleChange}
              disabled={!editable}
            />
          </FormControl>
          <FormControl>
            <Label>{t("screens.client.clientForm.ig")}</Label>
            <Input
              size="lg"
              name={"ig"}
              value={formik.values.ig}
              onChange={formik.handleChange}
              disabled={!editable}
            />
          </FormControl>
        </Stack>

        {isAdmin && (
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <FormControl
              isInvalid={
                Boolean(formik.getFieldMeta("note").error) &&
                formik.getFieldMeta("note").touched
              }
            >
              <Label>{t("screens.client.clientForm.note")}</Label>
              <Textarea
                name={"note"}
                value={formik.values.note}
                onChange={formik.handleChange}
                disabled={!editable}
              />
              <FormErrorMessage>
                {formik.getFieldMeta("note").error}
              </FormErrorMessage>
            </FormControl>
          </Stack>
        )}

        {editable && (
          <PrimayButton type={"submit"} isLoading={isSubmitting}>
            {t("common.save")}
          </PrimayButton>
        )}
        <>
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={onClose}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Attenzione
                </AlertDialogHeader>

                <AlertDialogBody>
                  Servizio dedicato esclusivamente a persone di sesso femminile.
                  Con la conferma del sesso, si effettua autocertificazione. Le
                  false dichiarazioni verranno perseguite ai sensi della legge.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button colorScheme="red" onClick={onClose} ml={3}>
                    Capito
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      </Stack>
    </form>
  );
};
